import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Subject, Observable, firstValueFrom } from 'rxjs';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface AlertMessage {
  type: AlertType;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private alertSubject = new Subject<AlertMessage>();
  private errors: any = {};
  private http: HttpClient;
  private platformId = inject(PLATFORM_ID);

  constructor(handler: HttpBackend) {
    this.http = new HttpClient(handler);
    this.loadErrors();
  }

  private loadErrors(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.http.get('assets/i18n/errors.json').subscribe({
      next: (data) => this.errors = data,
      error: () => {
        this.http.get('/assets/i18n/errors.json').subscribe({
          next: (data) => this.errors = data,
          error: (err) => console.warn('AlertService: Could not load errors.json', err)
        });
      }
    });
  }

  get alerts$(): Observable<AlertMessage> {
    return this.alertSubject.asObservable();
  }

  getErrorMessage(code: string): string {
    return this.errors[code] || code;
  }

  errorByCode(code: string, details?: any): void {
    let message = this.getErrorMessage(code);

    if (details && typeof details === 'object') {
      // VALIDATION_ERROR formatını dəstəkləyirik
      const errorMessages: string[] = [];
      Object.keys(details).forEach(key => {
        const val = details[key];
        if (Array.isArray(val)) {
          val.forEach(err => {
            if (err.message) errorMessages.push(err.message);
          });
        }
      });

      if (errorMessages.length > 0) {
        // Dublikatları təmizləyirik (məsələn: VALIDATION_ERROR bir neçə sahədə ola bilər)
        const uniqueMessages = Array.from(new Set(errorMessages));
        message = uniqueMessages.map(m => this.getErrorMessage(m)).join('\n');
      }
    } else if (typeof details === 'string') {
      message = details;
    }

    this.error(message || 'Gözlənilməz xəta baş verdi.');
  }

  success(message: string): void {
    this.alertSubject.next({ type: 'success', message });
  }

  error(message: string): void {
    this.alertSubject.next({ type: 'error', message });
  }

  warning(message: string): void {
    this.alertSubject.next({ type: 'warning', message });
  }

  info(message: string): void {
    this.alertSubject.next({ type: 'info', message });
  }
}
